import { Button } from "@/components";
import { useScrollDepth } from "@/hooks";
import { trackEvent } from "@/lib/dataLayer";
import {
  ClientLogos,
  FAQ,
  Header,
  MetaPartnership,
} from "@/pages/landingPage/components";
import type { FAQItem } from "@/pages/landingPage/components";
import { BrazilMap } from "@/pages/landingPage/components/brazilMap";
import { ReguaLeadForm } from "./components/reguaLeadForm";
import {
  animate,
  motion,
  useInView,
  useMotionValue,
  useTransform,
} from "framer-motion";
import {
  BellOff,
  CheckCircle2,
  CircleDollarSign,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";

const MERRIWEATHER = { fontFamily: "var(--font-merriweather)" } as const;

// Destaque animado com o número "39,4" em foco — mesmo tratamento visual do
// RecoveredHighlight institucional da home, mas com os dizeres específicos
// pedidos pelo marketing para esta LP (não reutiliza o componente da home
// porque o texto ali vem fixo das traduções institucionais).
function RecoveredHighlightCustom() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });

  const count = useMotionValue(0);
  const formatted = useTransform(count, latest =>
    latest.toFixed(1).replace(".", ",")
  );

  useEffect(() => {
    if (!inView) return;
    const controls = animate(count, 39.4, { duration: 2.2, ease: "easeIn" });
    return () => controls.stop();
  }, [inView, count]);

  return (
    <div
      ref={ref}
      className="flex flex-col items-center py-8 text-center lg:py-12"
    >
      <span className="text-lg font-semibold text-gray-700 sm:text-xl">
        Mais de R$
      </span>

      <span className="sr-only">
        Mais de R$ 39,4 milhões recuperados para o varejo brasileiro
      </span>

      <motion.span
        aria-hidden="true"
        style={MERRIWEATHER}
        className="text-primary text-[5rem] leading-none font-bold sm:text-[8rem] lg:text-[11rem]"
      >
        {formatted}
      </motion.span>

      <span
        aria-hidden="true"
        style={MERRIWEATHER}
        className="mt-2 text-3xl leading-tight font-bold text-black sm:text-4xl lg:text-5xl"
      >
        milhões recuperados
        <br />
        para o varejo brasileiro
      </span>
    </div>
  );
}

// Linha de KPI animada (número contando + label) — mesmo padrão visual do
// BigNumbers institucional da home (divisores, dt à esquerda/dd à direita),
// mas só com os 3 números que a equipe de marketing definiu para esta LP.
function StatsRow({
  label,
  value,
  suffix = "",
}: {
  label: string;
  value: number;
  suffix?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const count = useMotionValue(0);
  const formatted = useTransform(count, latest =>
    Math.round(latest).toLocaleString("pt-BR")
  );

  useEffect(() => {
    if (!inView) return;
    const controls = animate(count, value, { duration: 1.8, ease: "easeOut" });
    return () => controls.stop();
  }, [inView, value, count]);

  return (
    <div
      ref={ref}
      className="flex flex-col gap-2 border-b border-gray-200 py-7 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8 sm:py-8"
    >
      <dt className="text-primary max-w-xs text-base font-medium sm:text-lg">
        {label}
      </dt>
      <dd
        style={MERRIWEATHER}
        className="text-4xl font-bold tracking-tight text-black tabular-nums sm:text-right sm:text-5xl lg:text-6xl"
      >
        <motion.span>{formatted}</motion.span>
        {suffix}
      </dd>
    </div>
  );
}

const SITE_URL = "https://fidexa.com.br";
const PATH = "/lojistas-que-vendem-no-crediario-proprio";
// Sem o sufixo "| Fidexa" aqui: o <Helmet titleTemplate="%s | Fidexa"> global
// (src/App.tsx) já concatena o sufixo no <title> renderizado. og:title/
// twitter:title não passam pelo titleTemplate, então usam OG_TITLE (com
// sufixo) separadamente. Mesmo padrão de /cobranca-whatsapp e /crediario.
const TITLE = "Régua de Cobrança para Crediário Próprio"; // ≤ 60 chars com sufixo do template
const OG_TITLE = `${TITLE} | Fidexa`;
const DESCRIPTION =
  "Automatize a régua de cobrança do crediário próprio da sua loja: lembrete antes do vencimento e cobrança automática no atraso, tudo pelo WhatsApp oficial."; // ~157 chars

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Início", item: `${SITE_URL}/` },
    {
      "@type": "ListItem",
      position: 2,
      name: "Régua de Cobrança",
      item: `${SITE_URL}${PATH}`,
    },
  ],
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Régua de cobrança automatizada para crediário próprio",
  serviceType:
    "Automação de lembrete e cobrança de crediário próprio via WhatsApp",
  provider: { "@type": "Organization", name: "Fidexa", url: SITE_URL },
  areaServed: "BR",
  description: DESCRIPTION,
  inLanguage: "pt-BR",
  url: `${SITE_URL}${PATH}`,
};

const AGITATION_POINTS = [
  {
    icon: BellOff,
    text: "Parcelas esquecidas e clientes que pagariam, se fossem lembrados no tempo certo",
  },
  {
    icon: TrendingUp,
    text: "Atraso acumulando mês após mês, sem controle real do tamanho",
  },
  {
    icon: TrendingDown,
    text: "Caixa pressionado, com menos dinheiro entrando do que deveria",
  },
  {
    icon: CircleDollarSign,
    text: "Custos operacionais cada vez maiores só pra dar conta da cobrança manual",
  },
];

const PILLARS = [
  {
    title: "Antes e depois, tudo automático",
    desc: "Lembrete antes do vencimento, cobrança automática se atrasar. Sua equipe não precisa lembrar de nada disso.",
  },
  {
    title: "Você vê a inadimplência antes dela virar prejuízo",
    desc: "O painel mostra o que está parado em tempo real, não só no fechamento de caixa.",
  },
  {
    title: "Seu time continua vendendo, não cobrando",
    desc: "A régua roda sozinha; sua equipe fica livre para focar no que gera receita.",
  },
];

const STATS = [
  { label: "mensagens enviadas", value: 1496136 },
  { label: "acordos fechados", value: 480345 },
  { label: "de leitura no WhatsApp", value: 98, suffix: "%" },
];

const FAQ_ITEMS: FAQItem[] = [
  {
    question: "A Fidexa oferece crédito?",
    answer:
      "Não. A Fidexa não faz empréstimos, financiamentos, nem oferece crédito. Automatizamos a cobrança de vendas que você já fez no crediário da sua própria loja.",
  },
  {
    question: "Como a Fidexa funciona, na prática?",
    answer:
      "Ela acessa o cadastro de quem comprou no crediário da sua loja, identifica quem está com parcela em atraso e dispara a cobrança automática pelo WhatsApp, seguindo a régua de tempo que você define: 5 dias, 15 dias, 30 dias. Você continua no controle total das regras; a Fidexa só executa a cobrança de forma organizada, no momento certo.",
  },
  {
    question: "Minha equipe vai ter trabalho pra aprender isso?",
    answer:
      "Não. Nosso time importa sua base, configura o painel e treina sua equipe, sem travar o dia a dia da loja.",
  },
  {
    question: "Quanto custa?",
    answer:
      "Depende do volume de clientes da sua operação. Durante o diagnóstico gratuito, você recebe uma demonstração personalizada e já sai sabendo exatamente o investimento, sem contrato engessado.",
  },
  {
    question: "Minha loja é pequena, ainda assim vale a pena?",
    answer:
      "Se você vende no crediário próprio e sente que a inadimplência está crescendo junto com as vendas, vale a conversa. É isso que a demonstração resolve.",
  },
  {
    question: "Meus dados e os dos meus clientes estão seguros?",
    answer:
      "Sim. Conformidade com a LGPD e API oficial do WhatsApp (Meta Business Partner).",
  },
];

// CTAs com frases longas: no mobile viram pílula w-full com o texto podendo
// quebrar em 2 linhas (evita overflow horizontal do botão passando do
// container); a partir de sm voltam a ser w-auto numa linha só, como o resto
// do site.
const CTA_BUTTON_CLASS =
  "h-auto min-h-12 w-full rounded-full px-6 py-3 text-center text-base whitespace-normal sm:h-12 sm:w-auto sm:whitespace-nowrap sm:px-7";

export const ReguaCobranca = () => {
  useScrollDepth();

  const scrollToForm = (label: string) => {
    trackEvent({ event: "cta_click_hero", cta_label: label });
    document
      .getElementById("formulario")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        {/* LP de tráfego pago: fora do índice (decisão do founder). O noindex
            sai no HTML estático porque a rota está nas ROUTES do prerender. */}
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content={DESCRIPTION} />
        <meta
          name="keywords"
          content="régua de cobrança automatizada, cobrança crediário próprio, sistema de cobrança para lojista, recuperação de inadimplência, cobrança automática whatsapp"
        />
        <link rel="canonical" href={`${SITE_URL}${PATH}`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fidexa" />
        <meta property="og:title" content={OG_TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}${PATH}`} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={OG_TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      {/* overflow-x-hidden só até lg: abaixo de lg a imagem do hero não é
          sticky, então o corte horizontal (necessário para conter elementos
          decorativos que vazam) não tem custo. A partir de lg viramos
          overflow-visible nos dois eixos — por spec, restringir overflow em
          só um eixo força o outro a virar "auto" (deixa de ser "visible"),
          o que quebra o position:sticky da imagem do hero nos descendentes. */}
      <div className="min-h-screen max-w-screen overflow-x-hidden bg-white lg:overflow-visible">
        <Header variant="minimal" />

        <main className="flex flex-col gap-28 sm:gap-40">
          {/* DOBRA 1 — Hero */}
          <section
            aria-labelledby="hero-title"
            className="mx-auto w-full max-w-6xl px-4 sm:px-0"
          >
            <div className="flex min-h-screen items-center pt-24 pb-12 lg:pt-28">
              <div className="grid w-full items-center gap-10 lg:grid-cols-2 lg:items-start lg:gap-12">
                <div className="flex flex-col gap-4 text-left sm:gap-6 lg:gap-8">
                  <motion.span
                    className="text-primary text-xs font-semibold tracking-wide uppercase sm:text-sm"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  >
                    Para lojistas que vendem no crediário próprio
                  </motion.span>

                  <motion.h1
                    id="hero-title"
                    className="lg:text-title-xl text-[1.75rem] leading-[1.2] font-bold text-black sm:text-[2.25rem] sm:leading-[1.15] lg:leading-[1.08]"
                    style={{ fontFamily: "var(--font-family-regrade)" }}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                  >
                    Quanto mais sua loja vende no crediário próprio, mais rápido
                    cresce a inadimplência, pois a cobrança do carnê manual não
                    acompanha esse ritmo.
                  </motion.h1>

                  <motion.p
                    className="max-w-[560px] text-sm text-gray-700 sm:text-base"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                  >
                    A Fidexa é um sistema de cobrança para lojas que vendem no
                    crediário próprio — não oferecemos crédito. Automatizamos
                    sua régua completa: do lembrete antes do carnê vencer até a
                    cobrança automática assim que ele atrasa, tudo pelo WhatsApp
                    oficial. Sua loja cresce, a cobrança acompanha esse ritmo, e
                    a inadimplência para de comer sua margem sozinha.
                  </motion.p>

                  <motion.ul
                    className="flex flex-col gap-2 sm:gap-3"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
                  >
                    {[
                      "Sua inadimplência para de crescer na mesma velocidade das suas vendas",
                      "Cada carnê atrasado é cobrado automaticamente, sem depender de um funcionário lembrar",
                      "Sua equipe vende mais enquanto a Fidexa faz a cobrança",
                      "Diagnóstico gratuito em aproximadamente 30 minutos",
                    ].map(bullet => (
                      <li key={bullet} className="flex items-start gap-3">
                        <CheckCircle2 className="text-primary mt-0.5 size-5 shrink-0 sm:size-6" />
                        <span className="text-sm text-gray-700 sm:text-base">
                          {bullet}
                        </span>
                      </li>
                    ))}
                  </motion.ul>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                  >
                    <Button
                      className={CTA_BUTTON_CLASS}
                      onClick={() =>
                        scrollToForm("Quero recuperar minha inadimplência")
                      }
                    >
                      Quero recuperar minha inadimplência
                    </Button>
                  </motion.div>
                </div>

                <motion.div
                  className="flex justify-center lg:sticky lg:top-28 lg:justify-end lg:self-start"
                  initial={{ opacity: 0, scale: 0.9, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                >
                  <img
                    src="/images/header-fidexa.png"
                    alt="Print do chat de cobrança da Fidexa no WhatsApp, com a régua de cobrança enviando lembrete e cobrança automática de carnê ao cliente"
                    width={748}
                    height={735}
                    className="h-auto w-full max-w-md"
                    fetchPriority="high"
                  />
                </motion.div>
              </div>
            </div>
          </section>

          {/* DOBRA 2 — Prova social imediata */}
          <section aria-labelledby="prova-social-title">
            <h2 id="prova-social-title" className="sr-only">
              Prova social e parceiros
            </h2>

            <div className="mx-auto w-full max-w-6xl px-4 sm:px-0">
              <MetaPartnership showSubtitle={false} />

              <div className="mt-12 sm:mt-16">
                <RecoveredHighlightCustom />
              </div>
            </div>

            <ClientLogos />
          </section>

          {/* DOBRA 3 — Agitação */}
          <section
            aria-labelledby="agitacao-title"
            className="mx-auto w-full max-w-6xl px-4 sm:px-0"
          >
            <h2
              id="agitacao-title"
              className="mb-6 text-2xl font-bold text-black sm:text-3xl lg:text-4xl"
              style={MERRIWEATHER}
            >
              Vender no crediário não é o problema. Cobrar centenas de carnês na
              mão, sim.
            </h2>

            <p className="mb-8 w-full text-lg text-gray-700">
              Quanto mais sua loja cresce, mais parcelas aparecem para
              acompanhar, mas o tempo do seu time continua o mesmo. É aí que a
              inadimplência começa a corroer o caixa: não por falta de atenção,
              mas porque processo manual não acompanha o ritmo do próprio
              crescimento da loja.
            </p>

            <ul className="mb-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
              {AGITATION_POINTS.map(point => (
                <li key={point.text} className="flex items-start gap-3">
                  <point.icon className="text-primary mt-0.5 size-6 shrink-0" />
                  <span className="text-lg text-gray-700">{point.text}</span>
                </li>
              ))}
            </ul>

            <div className="rounded-2xl bg-gray-100 p-8 sm:p-10">
              <p className="text-xl font-bold text-black">
                Não é falta de organização. É que processos manuais não escalam.
              </p>
              <p className="mt-3 text-lg text-gray-700">
                Quanto mais sua loja vende no crediário, maior fica a
                necessidade de um sistema de cobrança automatizado.
              </p>
            </div>
          </section>

          {/* DOBRA 4 — Solução */}
          <section
            aria-labelledby="solucao-title"
            className="mx-auto w-full max-w-6xl px-4 sm:px-0"
          >
            <h2
              id="solucao-title"
              className="mb-6 text-2xl font-bold text-black sm:text-3xl lg:text-4xl"
              style={MERRIWEATHER}
            >
              Antes de vencer, a Fidexa já lembrou seu cliente. Sua cobrança
              começa antes do atraso acontecer.
            </h2>
            <p className="mb-12 w-full text-lg text-gray-700">
              Cliente lembrado no tempo certo paga mais. Cliente que atrasa, a
              régua cobra sozinha. Sua equipe não entra em nenhuma das duas
              etapas.
            </p>

            <ul className="mb-20 grid grid-cols-1 gap-6 lg:grid-cols-3">
              {PILLARS.map(pillar => (
                <li
                  key={pillar.title}
                  className="flex flex-col gap-3 rounded-2xl bg-gray-100 p-6"
                >
                  <h3 className="text-lg font-bold text-black">
                    {pillar.title}
                  </h3>
                  <p className="text-base text-gray-700">{pillar.desc}</p>
                </li>
              ))}
            </ul>

            <dl className="border-t border-gray-200">
              {STATS.map(stat => (
                <StatsRow key={stat.label} {...stat} />
              ))}
            </dl>

            <div className="mt-12">
              <Button
                className={CTA_BUTTON_CLASS}
                onClick={() =>
                  scrollToForm("Quero parar a inadimplência da minha loja")
                }
              >
                Quero parar a inadimplência da minha loja
              </Button>
            </div>
          </section>

          {/* DOBRA 5 — Prova social (case Lojas Elder) */}
          <section
            aria-labelledby="case-title"
            className="rounded-[2rem] bg-black px-4 sm:px-6 lg:px-12"
          >
            <div className="mx-auto grid max-w-6xl items-center gap-12 py-16 lg:grid-cols-2 lg:gap-16 lg:py-24">
              <motion.div
                className="flex flex-col gap-6 text-left"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <img
                  src="/landing-page/partners/lojas-elder-cliente-fidexa.png"
                  alt="Lojas Elder"
                  width={179}
                  height={59}
                  className="h-[59px] w-[179px] object-contain brightness-0 invert"
                />

                <h2
                  id="case-title"
                  className="text-2xl font-bold text-white sm:text-3xl lg:text-4xl"
                  style={MERRIWEATHER}
                >
                  De 14% pra 8% de inadimplência, sem contratar nenhum
                  funcionário
                </h2>

                <p className="text-lg text-gray-300">
                  A rede Lojas Elder trocou a cobrança manual pela régua
                  automática da Fidexa. Resultado: menos atraso, mais
                  previsibilidade de caixa, e o time financeiro do mesmo tamanho
                  de sempre.
                </p>

                <blockquote className="text-base text-gray-300 sm:text-lg">
                  &ldquo;Com mensagens automáticas e personalizadas, o time
                  financeiro conseguiu recuperar clientes e reduzir atrasos sem
                  aumentar custos operacionais.&rdquo;
                </blockquote>

                <div>
                  <Button
                    className={CTA_BUTTON_CLASS}
                    onClick={() =>
                      scrollToForm("Quero um resultado assim para minha loja")
                    }
                  >
                    Quero um resultado assim para minha loja
                  </Button>
                </div>
              </motion.div>

              <motion.div
                className="flex justify-center"
                initial={{ opacity: 0, scale: 0.8, y: 30 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <div className="w-full max-w-sm sm:max-w-md lg:max-w-xl">
                  <BrazilMap />
                </div>
              </motion.div>
            </div>
          </section>

          {/* DOBRA 6 — Quebra de objeções */}
          <section className="px-4 sm:px-0">
            <div className="mx-auto max-w-6xl">
              <FAQ
                items={FAQ_ITEMS}
                headingOverride="Dúvidas de quem vende no crediário próprio"
              />
            </div>
          </section>

          {/* DOBRA 7 — Formulário final */}
          <section
            id="formulario"
            aria-labelledby="formulario-title"
            className="px-4 sm:px-0"
          >
            <div className="mx-auto max-w-3xl">
              <div className="rounded-[2rem] bg-gray-50 p-2 shadow-sm sm:p-3">
                <div className="rounded-[1.75rem] border border-gray-200 bg-[#fff] px-6 py-16 shadow-sm sm:px-10 sm:py-20">
                  <div className="mb-10 flex flex-col gap-2 text-center">
                    <h2
                      id="formulario-title"
                      className="text-2xl font-bold text-black sm:text-3xl"
                      style={MERRIWEATHER}
                    >
                      Descubra quanto sua loja pode recuperar de inadimplência
                    </h2>
                    <p className="text-lg text-gray-700">
                      Preencha o formulário e fale agora com um especialista
                    </p>
                  </div>

                  <ReguaLeadForm />
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
};
